import React, { useState, useEffect } from 'react'
import {
  Calendar,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Table from '../../components/common/Table'
import Card from '../../components/common/Card'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { periodoService } from '../../services/periodoService'
import { usePermissions } from '../../hooks/usePermissions'

const Periodos = () => {
  const { canView, canCreate, canEdit, canDelete } = usePermissions()
  const [periodos, setPeriodos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalPeriodos, setTotalPeriodos] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [editingPeriodo, setEditingPeriodo] = useState(null)
  const [viewingPeriodo, setViewingPeriodo] = useState(null)

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: 'onChange'
  })

  useEffect(() => {
    fetchPeriodos()
  }, [currentPage, perPage, searchTerm])

  // Reset a página 1 cuando cambia la búsqueda
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const fetchPeriodos = async () => {
    try {
      setLoading(true)
      const response = await periodoService.getPeriodos({
        page: currentPage,
        per_page: perPage,
        search: searchTerm
      })
      
      if (response.success && response.data) {
        setPeriodos(response.data.data || [])
        setTotalPages(response.data.last_page || 1)
        setTotalPeriodos(response.data.total || 0)
      } else {
        toast.error(response.message || 'Error al cargar períodos')
        setPeriodos([])
        setTotalPages(1)
        setTotalPeriodos(0)
      }
    } catch (error) {
      toast.error('Error al cargar períodos')
      setPeriodos([])
      setTotalPages(1)
      setTotalPeriodos(0)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (periodo) => {
    setEditingPeriodo(periodo)
    reset({
      nombre: periodo.nombre,
      descripcion: periodo.descripcion || ''
    })
    setShowModal(true)
  }

  const handleView = (periodo) => {
    setViewingPeriodo(periodo)
    setShowViewModal(true)
  }

  const handleDelete = async (periodo) => {
    if (!window.confirm(`¿Está seguro de eliminar el período "${periodo.nombre}"?`)) {
      return
    }

    try {
      const response = await periodoService.deletePeriodo(periodo.id)
      if (response.success) {
        toast.success(response.message)
        fetchPeriodos()
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      toast.error('Error al eliminar período')
    }
  }

  const handleNew = () => {
    setEditingPeriodo(null)
    reset({
      nombre: '',
      descripcion: ''
    })
    setShowModal(true)
  }

  const onSubmit = async (data) => {
    if (!data.nombre || data.nombre.trim() === '') {
      toast.error('El nombre es obligatorio')
      return
    }

    try {
      setLoading(true)
      let response

      if (editingPeriodo) {
        response = await periodoService.updatePeriodo(editingPeriodo.id, data)
      } else {
        response = await periodoService.createPeriodo(data)
      }

      if (response.success) {
        toast.success(response.message || (editingPeriodo ? 'Período actualizado' : 'Período creado'))
        setShowModal(false)
        reset()
        setEditingPeriodo(null)
        await fetchPeriodos()
      } else {
        toast.error(response.message || 'Error al guardar período')
        if (response.errors) {
          Object.values(response.errors).flat().forEach(err => toast.error(err))
        }
      }
    } catch (error) {
      toast.error('Error al guardar período')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
            <Calendar className="h-5 w-5" />
          </div>
          <span className="font-medium text-gray-900 dark:text-gray-100">{row.nombre}</span>
        </div>
      )
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      render: (row) => (
        <span className="text-gray-600 dark:text-gray-400">
          {row.descripcion || 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center space-x-2">
          {canView('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleView(row)
              }}
              className="p-2 rounded-xl text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title="Ver detalles"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {canEdit('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleEdit(row)
              }}
              className="p-2 rounded-xl text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 transition-colors"
              title="Editar"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {canDelete('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDelete(row)
              }}
              className="p-2 rounded-xl text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
              title="Eliminar"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4 mb-4 sm:mb-0">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Períodos Académicos</h1>
            <p className="text-gray-600 dark:text-gray-400">Gestiona los períodos académicos del sistema</p>
          </div>
        </div>
        {canCreate('programas') && (
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={handleNew}
          >
            Nuevo Período
          </Button>
        )}
      </div>

      {/* Estadística */}
      <Card className="gradient" shadow="glow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Total Períodos</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalPeriodos}</p>
          </div>
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow">
            <Calendar className="h-6 w-6 text-white" />
          </div>
        </div>
      </Card>

      {/* Búsqueda */}
      <Card className="gradient" shadow="glow-lg">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar períodos..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Tabla */}
      <Card className="gradient" shadow="glow-lg">
        <Table
          columns={columns}
          data={periodos}
          loading={loading}
          emptyMessage="No se encontraron períodos"
          hover
          striped
          currentPage={currentPage}
          totalPages={totalPages}
          perPage={perPage}
          onPageChange={(page) => setCurrentPage(page)}
          onPerPageChange={(newPerPage) => {
            setPerPage(newPerPage)
            setCurrentPage(1)
          }}
        />
      </Card>

      {/* Modal Crear/Editar */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingPeriodo(null)
          reset()
        }}
        title={editingPeriodo ? 'Editar Período' : 'Nuevo Período'}
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre *
            </label>
            <Input
              type="text"
              {...register('nombre', { required: 'El nombre es requerido' })}
              placeholder="Ej: 1er Trimestre"
              error={errors.nombre?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción
            </label>
            <Input
              type="text"
              {...register('descripcion')}
              placeholder="Descripción del período"
              error={errors.descripcion?.message}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowModal(false)
                setEditingPeriodo(null)
                reset()
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
            >
              {editingPeriodo ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Ver */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Detalles del Período"
        size="md"
      >
        {viewingPeriodo && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Nombre</p>
              <p className="text-gray-900 dark:text-gray-100">{viewingPeriodo.nombre}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Descripción</p>
              <p className="text-gray-900 dark:text-gray-100">{viewingPeriodo.descripcion || 'N/A'}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Periodos
